import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import "../../Modal/modal.css";
import "../../css/unzip.css";
import PearsonLogo from "../../../../images/pearson_logo.png";
import icon_unzip from "../../../../images/icon_unzip.gif";
import apiService from "../../../utils/apiService";
import { UNZIP_API, UNZIP_PHP_RESTRICTION_MESSAGE } from "../../../../Constants";

const Unzip = ({ isOpen, onRequestClose, currentPath, unzipFile }) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [showReplacePopup, setShowReplacePopup] = useState(false);
    const [showFileReplacePopup, setShowFileReplacePopup] = useState(false);
    const [showSuccessPopup, setShowSuccessPopup] = useState(false);
    const [unzippedFolder, setUnzippedFolder] = useState("");
    const [responseData, setResponseData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [showPhpRestrictionPopup, setShowPhpRestrictionPopup] = useState(false);
    const [phpRestrictionPaths, setPhpRestrictionPaths] = useState([]);
    const [phpRestrictionMessage, setPhpRestrictionMessage] = useState(UNZIP_PHP_RESTRICTION_MESSAGE);

    const filesPerPage = 20;

    const onModalpopupClose = () => {
        if (searchParams.has("unzip")) {
            searchParams.delete("unzip");
            setSearchParams(searchParams);
        }
        onRequestClose(isOpen);
    };

    const cancelUnzipModal = () => onRequestClose(isOpen);

    const unzipFolder = async () => {
        try {
            setLoading(true);
            const res = await apiService.post(UNZIP_API, {
                current_path: currentPath,
                unzipfile: unzipFile,
            });
            const data = res.data;
            setLoading(false);

            if (data?.status_code === 409) {
                setResponseData(data);
                setUnzippedFolder(data?.existing_folder || unzipFile.replace(".zip", ""));
                if (Array.isArray(data?.conflicting_files) && data.conflicting_files.length > 0) {
                    setShowFileReplacePopup(true);
                } else {
                    setShowReplacePopup(true);
                }
            } else if (data?.status_code === 410) {
                setResponseData(data);
                setPage(1);
                setShowFileReplacePopup(true);
            } else if (data?.status_code === 200) {
                setUnzippedFolder(data?.extracted_folder || unzipFile.replace(".zip", ""));
                setShowSuccessPopup(true);
            } else if (data?.status_code === 403) {
                setPhpRestrictionMessage(data?.message || UNZIP_PHP_RESTRICTION_MESSAGE);
                setPhpRestrictionPaths(
                    Array.isArray(data?.data?.phpRestrictedPaths)
                        ? data.data.phpRestrictedPaths
                        : []
                );
                setShowPhpRestrictionPopup(true);
            } else {
                alert(data?.message || "An error occurred while unzipping.");
            }
        } catch (err) {
            setLoading(false);
            console.error("Unzip error:", err);
            alert("An error occurred while unzipping.");
        }
    };

    const confirmReplace = async () => {
        try {
            setLoading(true);
            const res = await apiService.post(UNZIP_API, {
                current_path: currentPath,
                unzipfile: unzipFile,
                replace: true,
            });
            const data = res.data;
            setLoading(false);

            if (data?.status_code === 200) {
                setUnzippedFolder(data?.extracted_folder || unzipFile.replace(".zip", ""));
                setShowReplacePopup(false);
                setShowFileReplacePopup(false);
                setShowSuccessPopup(true);
            } else if (data?.status_code === 403) {
                setShowReplacePopup(false);
                setShowFileReplacePopup(false);
                setPhpRestrictionMessage(data?.message || UNZIP_PHP_RESTRICTION_MESSAGE);
                setPhpRestrictionPaths(
                    Array.isArray(data?.data?.phpRestrictedPaths)
                        ? data.data.phpRestrictedPaths
                        : []
                );
                setShowPhpRestrictionPopup(true);
            } else {
                alert(data?.message || "Failed to replace folder or files.");
            }
        } catch (err) {
            setLoading(false);
            console.error("Replace error:", err);
            alert("Failed to replace folder or files.");
        }
    };

    const cancelReplace = () => {
        setShowReplacePopup(false);
        setShowFileReplacePopup(false);
    };

    const closeSuccessPopup = () => {
        setShowSuccessPopup(false);
        onModalpopupClose();
    };

    const closePhpRestrictionPopup = () => {
        setShowPhpRestrictionPopup(false);
        setPhpRestrictionPaths([]);
        setPhpRestrictionMessage(UNZIP_PHP_RESTRICTION_MESSAGE);
        onModalpopupClose();
    };

    const getTopLevelEntries = (files = []) => {
        const topLevel = new Map();
        if (!Array.isArray(files)) return [];
        files.forEach((path) => {
            if (!path) return;
            const parts = path.split("/");
            if (parts.length > 1) topLevel.set(parts[0] + "/", "folder");
            else if (path.endsWith("/")) topLevel.set(path, "folder");
            else topLevel.set(path, "file");
        });
        if (topLevel.size === 0 && files.length === 1) {
            const name = files[0];
            topLevel.set(name.endsWith("/") ? name : name + "/", "folder");
        }
        return Array.from(topLevel.entries());
    };

    const entries = getTopLevelEntries(responseData?.conflicting_files);
    const totalPages = Math.ceil(entries.length / filesPerPage);
    const start = (page - 1) * filesPerPage;
    const end = start + filesPerPage;
    const currentEntries = entries.slice(start, end);
    const left = currentEntries.slice(0, 10);
    const right = currentEntries.slice(10, 20);

    return (
        <div className="modal-content-table">
            {/* --- Default Modal --- */}
            {!showReplacePopup && !showFileReplacePopup && !showSuccessPopup && !showPhpRestrictionPopup && (
                <>
                    <table border="0" width="99%">
                        <tbody>
                            <tr>
                                <td><span className="pagetitle">&nbsp;Unzip File</span></td>
                                <td align="right"><img src={PearsonLogo} alt="Pearson" width="85" height="21" /></td>
                                <td><span className="close" onClick={onModalpopupClose}>&times;</span></td>
                            </tr>
                        </tbody>
                    </table>

                    <div id="table" className="unzip-table">
                        <table border="0" width="99%">
                            <tbody>
                                <tr>
                                    <td width="90"><img src={icon_unzip} alt="Unzip" width="90" height="94" /></td>
                                    <td align="center" className="unzip-instructions">
                                        Warning! You have chosen to unzip
                                        <span className="text-red"> {unzipFile} </span> in the
                                        <span className="text-red"> {currentPath} </span> directory.
                                        Unzipping this file will expand all files contained within this zip into this directory.
                                        Do you wish to proceed?
                                    </td>
                                </tr>
                            </tbody>
                        </table>

                        <table width="50%" align="center">
                            <tbody>
                                <tr align="center">
                                    <td>
                                        <input type="button" className="unzip-formButton" value="Cancel" onClick={cancelUnzipModal} disabled={loading} />
                                    </td>
                                    <td>
                                        <button className="unzip-formButtonGo" onClick={unzipFolder} disabled={loading}>
                                            {loading ? <><span className="spinner-mini"></span> Unzipping...</> : "Unzip"}
                                        </button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            {/* --- Folder Replace Popup --- */}
            {showReplacePopup && (
                <>
                    <table border="0" width="99%">
                        <tbody>
                            <tr>
                                <td><span className="pagetitle">&nbsp;Folder Already Exists</span></td>
                                <td align="right"><img src={PearsonLogo} alt="Pearson" width="85" height="21" /></td>
                                <td><span className="close" onClick={onModalpopupClose}>&times;</span></td>
                            </tr>
                        </tbody>
                    </table>

                    <div id="table" className="unzip-table">
                        <table border="0" width="99%">
                            <tbody>
                                <tr>
                                    <td width="90">
                                        <img src={icon_unzip} alt="Unzip" width="90" height="94" />
                                    </td>
                                    <td align="center" className="instructions">
                                        <p style={{ fontWeight: "bold" }}>
                                            The folder <span className="text-red">{unzippedFolder}</span> already exists in this directory.
                                        </p>
                                        <p>Do you want to replace it?</p>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                        <table width="50%" align="center" className="button-table-replace">
                            <tbody>
                                <tr align="center">
                                    <td><input type="button" className="unzip-formButton" value="Cancel" onClick={cancelReplace} disabled={loading} /></td>
                                    <td>
                                        <button className="unzip-formButtonGo" onClick={confirmReplace} disabled={loading}>
                                            {loading ? <><span className="spinner-mini"></span> Replacing...</> : "Replace Folder"}
                                        </button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            {/* --- File Replace Popup --- */}
            {showFileReplacePopup && (
                <>
                    <table border="0" width="99%">
                        <tbody>
                            <tr>
                                <td><span className="pagetitle">&nbsp;Existing Files Found</span></td>
                                <td align="right"><img src={PearsonLogo} alt="Pearson" width="85" height="21" /></td>
                                <td><span className="close" onClick={onModalpopupClose}>&times;</span></td>
                            </tr>
                        </tbody>
                    </table>

                    <div id="table" className="unzip-file-popup">
                        <p className="instructions file-popup-title">
                            Some files or folders already exist in this directory. Do you want to replace them?
                        </p>

                        <div className="file-list-container">
                            <ul className="file-list">
                                {left.map(([name, type], i) => (
                                    <li key={i} className={`file-item ${type}`}>
                                        <span>{type === "folder" ? "📁" : "📄"}</span>
                                        <span>{name}</span>
                                    </li>
                                ))}
                            </ul>
                            <ul className="file-list">
                                {right.map(([name, type], i) => (
                                    <li key={i + 10} className={`file-item ${type}`}>
                                        <span>{type === "folder" ? "📁" : "📄"}</span>
                                        <span>{name}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {totalPages > 1 && (
                            <div className="pagination">
                                <span className="page-nav" onClick={() => page > 1 && setPage(page - 1)}>‹</span>
                                <span className="page-info">Page {page} of {totalPages}</span>
                                <span className="page-nav" onClick={() => page < totalPages && setPage(page + 1)}>›</span>
                            </div>
                        )}

                        <table width="60%" align="center" className="button-table">
                            <tbody>
                                <tr align="center">
                                    <td><input type="button" className="unzip-formButton" value="Cancel" onClick={cancelReplace} disabled={loading} /></td>
                                    <td>
                                        <button className="unzip-formButtonGo" onClick={confirmReplace} disabled={loading}>
                                            {loading ? <><span className="spinner-mini"></span> Replacing...</> : "Replace Files"}
                                        </button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            {/* --- PHP Restriction Popup --- */}
            {showPhpRestrictionPopup && (
                <>
                    <table border="0" width="99%" role="presentation">
                        <tbody>
                            <tr>
                                <td><span className="pagetitle">&nbsp;Unzip Blocked</span></td>
                                <td align="right"><img src={PearsonLogo} alt="Pearson" width="85" height="21" /></td>
                                <td>
                                    <button
                                        type="button"
                                        className="close"
                                        onClick={closePhpRestrictionPopup}
                                        aria-label="Close"
                                    >
                                        &times;
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>

                    <div id="table" className="unzip-table bulk-upload-filename-space-body">
                        <table border="0" width="99%" role="presentation">
                            <tbody>
                                <tr>
                                    <td width="90">
                                        <img src={icon_unzip} alt="Unzip" width="90" height="94" />
                                    </td>
                                    <td align="center" className="instructions delete-folder-modal-copy-delete">
                                        <p className="delete-folder-note bulk-upload-filename-space-note">
                                            <strong>Note:</strong>{' '}
                                            <strong className="delete-folder-alert-text">
                                                {phpRestrictionMessage}
                                            </strong>
                                        </p>
                                        <p className="delete-folder-path">
                                            <strong>Directory :</strong>{' '}
                                            <strong className="delete-folder-path-text">{currentPath}</strong>
                                        </p>
                                        {phpRestrictionPaths.length > 0 ? (
                                            <div className="delete-folder-lists bulk-upload-filename-space-lists">
                                                <div className="bulk-upload-filename-space-column">
                                                    <h3 className="delete-folder-list-heading">Files:</h3>
                                                    <ul className="delete-folder-list">
                                                        {phpRestrictionPaths.map((name, index) => (
                                                            <li key={`php-restriction-${index}`}>
                                                                <span style={{ fontWeight: 'bold' }}>
                                                                    {index + 1}. {name}
                                                                </span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            </div>
                                        ) : null}
                                    </td>
                                </tr>
                            </tbody>
                        </table>

                        <table width="50%" align="center" role="presentation">
                            <tbody>
                                <tr align="center">
                                    <td>
                                        <input
                                            type="button"
                                            className="unzip-formButton"
                                            value="Cancel"
                                            onClick={closePhpRestrictionPopup}
                                        />
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            {/* --- Success Popup --- */}
            {showSuccessPopup && (
                <>
                    <table border="0" width="99%">
                        <tbody>
                            <tr>
                                <td><span className="pagetitle">&nbsp;Unzip Successful</span></td>
                                <td align="right"><img src={PearsonLogo} alt="Pearson" width="85" height="21" /></td>
                                <td><span className="close" onClick={onModalpopupClose}>&times;</span></td>
                            </tr>
                        </tbody>
                    </table>

                    <div id="table" className="unzip-table">
                        <table border="0" width="99%">
                            <tbody>
                                <tr>
                                    <td width="90"><img src={icon_unzip} alt="Success" width="90" height="94" /></td>
                                    <td align="center" className="instructions">
                                        <p><b>All files and folders have been successfully extracted!</b></p>
                                        <p>They are now available in:<br /><span className="text-red">{currentPath}</span></p>
                                    </td>
                                </tr>
                            </tbody>
                        </table>

                        <table width="100%" align="center" className="success-button">
                            <tbody>
                                <tr align="center">
                                    <td>
                                        <input type="button" className="unzip-formButtonGo" value="OK" onClick={closeSuccessPopup} />
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    );
};

export default Unzip;
